import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Users,
  Clock,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  Trash2,
  RefreshCw,
  TrendingUp,
  HelpCircle,
} from 'lucide-react';
import { apiRequest } from '../../config/api';

const TIME_RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

function StatCard({ icon: Icon, label, value, subtext, color }) {
  const colorMap = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
    violet: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`p-1.5 rounded-lg ${colorMap[color] || colorMap.blue}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
        {value}
      </div>
      {subtext && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtext}</p>
      )}
    </div>
  );
}

function MiniBarChart({ data, label }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-gray-400 dark:text-gray-500">
        No data yet
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => d.conversations), 1);

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-[3px] h-28">
        {data.map((d, i) => {
          const height = Math.max((d.conversations / maxVal) * 100, 4);
          return (
            <div
              key={d.date}
              className="flex-1 group relative"
              style={{ minWidth: 0 }}
            >
              <div
                className="w-full rounded-t-sm bg-brand-500/80 dark:bg-brand-400/70 transition-colors group-hover:bg-brand-600 dark:group-hover:bg-brand-400"
                style={{ height: `${height}%` }}
              />
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-10">
                <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap shadow-lg">
                  <div className="font-medium">{new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <div>{d.conversations} conv &middot; {d.messages} msgs</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* X-axis labels */}
      <div className="flex justify-between px-0.5">
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {data.length > 0
            ? new Date(data[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : ''}
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {data.length > 0
            ? new Date(data[data.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : ''}
        </span>
      </div>
    </div>
  );
}

function SatisfactionBar({ positive, negative, total }) {
  if (total === 0) {
    return (
      <div className="text-xs text-gray-400 dark:text-gray-500">
        No ratings yet
      </div>
    );
  }

  const posPercent = Math.round((positive / total) * 100);
  const negPercent = 100 - posPercent;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <ThumbsUp className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{posPercent}%</span>
        </div>
        <div className="flex-1 h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full flex">
            <div
              className="bg-emerald-500 dark:bg-emerald-400 transition-all duration-500"
              style={{ width: `${posPercent}%` }}
            />
            <div
              className="bg-red-400 dark:bg-red-500 transition-all duration-500"
              style={{ width: `${negPercent}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-red-500 dark:text-red-400">{negPercent}%</span>
          <ThumbsDown className="w-3.5 h-3.5 text-red-400" />
        </div>
      </div>
      <div className="flex justify-between text-[11px] text-gray-400 dark:text-gray-500">
        <span>{positive} positive</span>
        <span>{total} total ratings</span>
        <span>{negative} negative</span>
      </div>
    </div>
  );
}

function KnowledgeGapsList({ gaps, chatbotId, onDismiss }) {
  if (!gaps || gaps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center">
        <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 mb-3">
          <ThumbsUp className="w-5 h-5 text-emerald-500" />
        </div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No knowledge gaps detected</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-[240px]">
          Your chatbot is answering questions well. Gaps appear when it can't find relevant information.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {gaps.map((gap) => (
        <div
          key={gap.id}
          className="group flex items-start gap-2.5 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 hover:border-gray-200 dark:hover:border-gray-600 transition-colors"
        >
          <div className={`p-1 rounded-md mt-0.5 flex-shrink-0 ${
            gap.gap_type === 'low_relevance'
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
              : 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400'
          }`}>
            {gap.gap_type === 'low_relevance' ? (
              <HelpCircle className="w-3 h-3" />
            ) : (
              <AlertCircle className="w-3 h-3" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug line-clamp-2">
              &ldquo;{gap.query}&rdquo;
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                gap.gap_type === 'low_relevance'
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              }`}>
                {gap.gap_type === 'low_relevance' ? 'Low relevance' : 'No info'}
              </span>
              {gap.max_relevance_score != null && (
                <span className="text-[10px] text-gray-400">
                  Score: {(gap.max_relevance_score * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => onDismiss(gap.id)}
            className="p-1 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
            title="Dismiss"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return 'N/A';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export default function ChatbotAnalyticsPanel({ chatbotId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(`/chatbots/${chatbotId}/analytics?days=${days}`);
      if (!response.ok) throw new Error('Failed to fetch analytics');
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [chatbotId, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleDismissGap = async (gapId) => {
    try {
      const response = await apiRequest(`/chatbots/${chatbotId}/knowledge-gaps/${gapId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setAnalytics((prev) => ({
          ...prev,
          knowledge_gaps: prev.knowledge_gaps.filter((g) => g.id !== gapId),
        }));
      }
    } catch (err) {
      console.error('Failed to dismiss gap:', err);
    }
  };

  // Loading skeleton
  if (loading && !analytics) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-8 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-xl" />
          ))}
        </div>
        <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-xl" />
        <div className="h-32 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Failed to load analytics
        </p>
        <p className="text-xs text-gray-400 mb-4">{error}</p>
        <button
          onClick={fetchAnalytics}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800 rounded-lg hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-5">
      {/* Header with time range selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-brand-600 dark:text-brand-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Analytics</h3>
          {loading && (
            <RefreshCw className="w-3 h-3 text-gray-400 animate-spin" />
          )}
        </div>
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          {TIME_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => setDays(range.value)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-all ${
                days === range.value
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={MessageSquare}
          label="Conversations"
          value={analytics.total_conversations.toLocaleString()}
          subtext={`${analytics.total_messages.toLocaleString()} messages`}
          color="blue"
        />
        <StatCard
          icon={Users}
          label="Unique Visitors"
          value={analytics.unique_visitors.toLocaleString()}
          color="violet"
        />
        <StatCard
          icon={Clock}
          label="Avg Duration"
          value={formatDuration(analytics.avg_duration_seconds)}
          color="amber"
        />
        <StatCard
          icon={ThumbsUp}
          label="Satisfaction"
          value={analytics.satisfaction_rate != null ? `${analytics.satisfaction_rate}%` : 'N/A'}
          subtext={analytics.satisfaction_total > 0 ? `${analytics.satisfaction_total} ratings` : 'No ratings yet'}
          color="emerald"
        />
      </div>

      {/* Conversations over time chart */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
          Conversations over time
        </h4>
        <MiniBarChart data={analytics.daily_analytics} />
      </div>

      {/* Satisfaction breakdown */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
          Satisfaction breakdown
        </h4>
        <SatisfactionBar
          positive={analytics.satisfaction_positive}
          negative={analytics.satisfaction_negative}
          total={analytics.satisfaction_total}
        />
      </div>

      {/* Knowledge gaps */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Knowledge gaps
          </h4>
          {analytics.knowledge_gaps.length > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              {analytics.knowledge_gaps.length} gap{analytics.knowledge_gaps.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <KnowledgeGapsList
          gaps={analytics.knowledge_gaps}
          chatbotId={chatbotId}
          onDismiss={handleDismissGap}
        />
      </div>
    </div>
  );
}
